import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { productsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ProductDetail = ({ cartCount, onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Load product from backend API
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setIsLoading(true);
        const data = await productsAPI.getById(id);
        setProduct(data);
      } catch (error) {
        console.error('Error loading product:', error);
        setProduct(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadProduct();
  }, [id]);
  
  // Support both old and new data structures
  const scentsAvailable = product?.scentsWithImages 
    ? product.scentsWithImages.map(s => s.scent)
    : (product?.scentsAvailable || [product?.scent]);
  
  const [selectedScent, setSelectedScent] = useState('');
  
  // Update selected scent when product loads
  useEffect(() => {
    if (product && scentsAvailable.length > 0) {
      setSelectedScent(scentsAvailable[0] || product?.scent || '');
    }
  }, [product]);
  
  // Get images for selected scent
  const getImagesForScent = (scent) => {
    if (product?.scentsWithImages) {
      const scentObj = product.scentsWithImages.find(s => s.scent === scent);
      return scentObj?.images || [];
    }
    return product?.images || [product?.image];
  };
  
  const currentImages = getImagesForScent(selectedScent);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar cartCount={cartCount} />
        <div className="container mx-auto px-6 py-20 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar cartCount={cartCount} />
        <div className="container mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-800 mb-4">Product Not Found</h2>
          <Button onClick={() => navigate('/shop')} className="bg-[#8B4545] hover:bg-[#7B3535]">
            Back to Shop
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    const productWithScent = {
      ...product,
      selectedScent: selectedScent,
      displayName: `${product.name} - ${selectedScent}`,
      image: currentImages[0],
      images: currentImages
    };
    onAddToCart(productWithScent);
    toast({
      title: 'Added to cart!',
      description: `${product.name} (${selectedScent}) has been added to your cart.`,
    });
  };

  // Get the main image for SEO
  const mainImage = currentImages[0] || '';
  
  // Generate product keywords based on product name and category - UK Handmade Focus
  const generateProductKeywords = () => {
    const baseKeywords = [
      product.name,
      `${product.name} UK`,
      `buy ${product.name} UK`,
      `${product.name} for sale UK`,
      'handmade gifts UK',
      'handmade home decor UK',
      'artisan handmade products UK',
      'unique handmade presents UK',
      'eco-friendly handmade gifts UK',
      'handmade craft shop UK',
      'personalised gifts UK',
      'handmade birthday gifts UK',
      'handmade wedding gifts UK',
      'handmade Christmas gifts UK'
    ];
    
    // Add scent keywords
    if (product.scentsWithImages) {
      product.scentsWithImages.forEach(s => {
        baseKeywords.push(`${s.scent} handmade candle UK`);
        baseKeywords.push(`${s.scent} scented candle`);
      });
    }
    
    // Add category-specific UK handmade keywords
    const name = product.name.toLowerCase();
    if (name.includes('elephant')) {
      baseKeywords.push('elephant candle UK', 'handmade elephant candle', 'decorative elephant gift UK', 'animal candle handmade', 'unique elephant gift UK');
    }
    if (name.includes('teddy') || name.includes('bear')) {
      baseKeywords.push('teddy bear candle UK', 'handmade bear candle', 'baby shower gift UK', 'nursery decor handmade', 'cute handmade gift UK');
    }
    if (name.includes('flower') || name.includes('rose')) {
      baseKeywords.push('flower candle UK', 'handmade floral candle', 'botanical gift UK', 'rose candle handmade', 'garden lover gift UK');
    }
    if (name.includes('angel')) {
      baseKeywords.push('angel candle UK', 'handmade angel wing candle', 'memorial candle UK', 'sympathy gift handmade', 'remembrance gift UK');
    }
    if (name.includes('resin') || name.includes('clock')) {
      baseKeywords.push('handmade resin clock UK', 'resin art UK', 'handmade clock gift', 'unique resin crafts UK', 'artisan resin UK');
    }
    
    // Add UK location keywords
    baseKeywords.push('handmade Aylesbury', 'handmade gifts Milton Keynes', 'artisan crafts Buckinghamshire', 'handmade Oxford');
    
    // Add occasion keywords
    baseKeywords.push('birthday gift handmade UK', 'wedding gift handmade', 'Christmas gift artisan UK', 'Mothers Day handmade gift');
    
    // Add material keywords
    baseKeywords.push('soya wax candle UK', 'eco-friendly candle', 'natural handmade candle', 'cotton wick candle');
    
    return baseKeywords.join(', ');
  };

  // Generate rich product description for SEO - UK Handmade Focus
  const generateSEODescription = () => {
    const scents = product.scentsWithImages?.map(s => s.scent).join(', ') || '';
    return `Buy ${product.name} - handmade in UK. ${product.description?.substring(0, 60) || 'Artisan crafted with love'}. Scents: ${scents}. £${product.price}. Free UK delivery £50+. Perfect gift!`;
  };

  return (
    <div className="min-h-screen bg-black">
      <SEO 
        title={`${product.name} | Buy Online UK`}
        description={generateSEODescription()}
        keywords={generateProductKeywords()}
        url={`/product/${product.id}`}
        image={mainImage}
        type="product"
        product={product}
      />
      <Navbar cartCount={cartCount} />

      <div className="container mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/shop')}
          className="flex items-center gap-2 text-amber-400 hover:text-amber-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Shop</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl overflow-hidden">
            <div className="relative aspect-square">
              <img
                src={currentImages[currentImageIndex] || product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/600x600?text=No+Image';
                }}
              />
              {currentImages.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev === 0 ? currentImages.length - 1 : prev - 1)}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev === currentImages.length - 1 ? 0 : prev + 1)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  >
                    →
                  </button>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {currentImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-amber-400' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="p-4 flex gap-2">
              {product.badges && product.badges.includes('New') && (
                <span className="bg-amber-400 text-black px-4 py-2 rounded-full text-sm font-semibold">
                  New
                </span>
              )}
              {product.badges && product.badges.includes('Bestseller') && (
                <span className="bg-amber-400 text-black px-4 py-2 rounded-full text-sm font-semibold">
                  Bestseller
                </span>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col justify-center">
            <p className="text-sm font-semibold text-amber-400 tracking-wider mb-2">
              {selectedScent || product.scent}
            </p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              {product.name}
            </h1>
            
            <div className="flex items-center gap-2 mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-gray-400 text-sm">(24 reviews)</span>
            </div>

            <div className="text-5xl font-bold text-amber-400 mb-6">
              £{product.price.toFixed(2)}
            </div>

            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              {product.description}
            </p>

            {/* Scent Selector */}
            {scentsAvailable && scentsAvailable.length > 1 && (
              <div className="mb-8 bg-gradient-to-br from-amber-400/10 to-amber-500/5 border-2 border-amber-400/30 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  <label className="block text-base font-bold text-amber-400">
                    Choose Your Scent for {product.name}
                  </label>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Select your preferred fragrance from the available options below
                </p>
                <Select value={selectedScent} onValueChange={(value) => {
                  setSelectedScent(value);
                  setCurrentImageIndex(0); // Reset to first image when scent changes
                }}>
                  <SelectTrigger className="w-full bg-gray-800 border-amber-400/50 text-white h-14 text-base font-semibold hover:border-amber-400 transition-colors">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-amber-400/50">
                    {scentsAvailable.map((scent) => (
                      <SelectItem 
                        key={scent} 
                        value={scent} 
                        className="text-white hover:bg-amber-400/20 focus:bg-amber-400/20 text-base py-3"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                          <span className="font-semibold">{scent}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-4 flex items-center gap-2 text-sm text-amber-400/80">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Your selected scent: <strong className="text-amber-400">{selectedScent}</strong></span>
                </div>
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-8">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Category</p>
                  <p className="font-semibold text-amber-400">{product.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Availability</p>
                  <p className="font-semibold text-green-400">
                    {product.inStock ? `In Stock (${product.stockCount})` : 'Out of Stock'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="flex-1 bg-amber-400 hover:bg-amber-500 text-black font-semibold py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                disabled={!product.inStock}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Additional Info */}
            <div className="mt-8 space-y-4">
              <div className="border-t border-gray-800 pt-4">
                <h3 className="font-semibold text-white mb-2">Features:</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-1">
                  <li>Handcrafted with premium materials</li>
                  <li>Natural soy wax blend</li>
                  <li>Long-lasting fragrance</li>
                  <li>Eco-friendly packaging</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;